import { useState, FormEvent } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Send, Mail, Building2 } from 'lucide-react'
import SEO from '../components/seo/SEO'
import Button from '../components/ui/Button'
import styles from './Contact.module.css'

const PROJECT_TYPES = ['Feasibility Study', 'Project Design (PDD)', 'Permitting & Compliance', 'Implementation', 'Monitoring & Verification', 'Carbon Credit Trading', 'Capacity Building', 'Biochar / BECCS', 'Blue Carbon', 'REDD+ / A/R', 'Other']

export default function Contact() {
  const { t } = useTranslation('contact')
  const { t: tSeo } = useTranslation('seo')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <div className={styles.page}>
      <SEO title={tSeo('contact.title')} description={tSeo('contact.description')} canonical="/contact" />

      <section className={styles.hero}>
        <div className={styles.container}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="eyebrow">{t('hero.eyebrow')}</span>
            <h1>{t('hero.title')}</h1>
            <p className={styles.lead}>{t('hero.lead')}</p>
          </motion.div>
        </div>
      </section>

      <section className={styles.formSection}>
        <div className={styles.container}>
          <div className={styles.layout}>
            <motion.div
              className={styles.formWrap}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {submitted ? (
                <div className={styles.success}>
                  <div className={styles.successIcon}><Send size={32} /></div>
                  <h3>{t('success.title')}</h3>
                  <p>{t('success.message')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className={styles.form}>
                  <div className={styles.fieldRow}>
                    <div className={styles.field}>
                      <label htmlFor="name">{t('form.name')}</label>
                      <input type="text" id="name" name="name" required placeholder={t('form.namePlaceholder')} />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="email">{t('form.email')}</label>
                      <input type="email" id="email" name="email" required placeholder={t('form.emailPlaceholder')} />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="organisation">{t('form.organisation')}</label>
                    <input type="text" id="organisation" name="organisation" placeholder={t('form.organisationPlaceholder')} />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="projectType">{t('form.projectType')}</label>
                    <select id="projectType" name="projectType" required>
                      <option value="">{t('form.projectTypePlaceholder')}</option>
                      {PROJECT_TYPES.map(pt => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="message">{t('form.message')}</label>
                    <textarea id="message" name="message" rows={5} required placeholder={t('form.messagePlaceholder')} />
                  </div>
                  <Button variant="primary" size="lg" type="submit">
                    {t('form.submit')} <Send size={16} />
                  </Button>
                </form>
              )}
            </motion.div>

            <motion.div
              className={styles.sidebar}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className={styles.sidebarCard}>
                <Mail size={20} />
                <h4>{t('sidebar.email')}</h4>
                <a href="mailto:hello@carbon13.co.uk">hello@carbon13.co.uk</a>
              </div>
              <div className={styles.sidebarCard}>
                <Building2 size={20} />
                <h4>{t('sidebar.office')}</h4>
                <p>Carbon13 Ltd</p>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>London, United Kingdom</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}
